import type {
  ExpressionSpecification,
  GeoJSONSourceSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';

import type { LatLonPoint } from '@/types/barriers';

import { WAYPOINT_OUTLINE_ICON_IMAGE_ID } from './waypointOutlineIcon';

export const BARRIER_SELECTED_WAYPOINTS_SOURCE_ID = 'barrier-selected-waypoints';
export const BARRIER_SELECTED_WAYPOINTS_LAYER_ID = 'barrier-selected-waypoints-dots';

/**
 * Per-feature properties on the selected-barrier waypoint source. The
 * `index` lets the UI map a clicked dot back to its row in the panel
 * list (future hook — currently unused).
 */
export interface BarrierSelectedWaypointProperties {
  readonly barrier_id: number;
  readonly index: number;
}

const ICON_SIZE: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 0.55,
  10, 0.8,
  14, 1.0,
];

export function buildEmptyBarrierSelectedWaypointsCollection(): FeatureCollection<
  Point,
  BarrierSelectedWaypointProperties
> {
  return { type: 'FeatureCollection', features: [] };
}

export function buildBarrierSelectedWaypointsSource(): GeoJSONSourceSpecification {
  return {
    type: 'geojson',
    data: buildEmptyBarrierSelectedWaypointsCollection(),
  };
}

/**
 * Outline-only diamond marking the currently-selected barrier's
 * `auto_waypoints`. Distinct visual from the solid detour layer so the
 * user reads it as "configured, awaiting a route to use it".
 */
export function buildBarrierSelectedWaypointsLayer(): SymbolLayerSpecification {
  return {
    id: BARRIER_SELECTED_WAYPOINTS_LAYER_ID,
    type: 'symbol',
    source: BARRIER_SELECTED_WAYPOINTS_SOURCE_ID,
    layout: {
      'icon-image': WAYPOINT_OUTLINE_ICON_IMAGE_ID,
      'icon-size': ICON_SIZE,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-opacity': 0.95,
    },
  };
}

export function buildBarrierSelectedWaypointsCollection(
  barrierId: number | null,
  waypoints: readonly LatLonPoint[] | null,
): FeatureCollection<Point, BarrierSelectedWaypointProperties> {
  if (barrierId === null || waypoints === null || waypoints.length === 0) {
    return buildEmptyBarrierSelectedWaypointsCollection();
  }
  const features: Feature<Point, BarrierSelectedWaypointProperties>[] =
    waypoints.map((wp, index) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [wp.lon, wp.lat] },
      properties: { barrier_id: barrierId, index },
    }));
  return { type: 'FeatureCollection', features };
}
